import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Wallet, Landmark } from "lucide-react";
import { AddressData } from "./AddressSelectionModal";

interface ProductPreview {
  image: string;
  title: string;
  price: string | number;
  color?: string;
  size?: string;
}

interface ResolutionConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onEditAddress?: () => void;
  submitting: boolean;
  returningProduct: ProductPreview;
  replacementProduct: ProductPreview;
  address: AddressData;
  phone?: string;
  type?: 'replacement' | 'store_credit' | 'refund';
}

const ResolutionConfirmationModal: React.FC<ResolutionConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onEditAddress,
  submitting,
  replacementProduct,
  address,
  phone,
  type = 'replacement'
}) => {
  const isRefund = type === 'refund' || type === 'store_credit';

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="tw-fixed tw-inset-0 tw-z-[100] tw-flex tw-items-center tw-justify-center tw-p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="tw-absolute tw-inset-0 tw-bg-black/90 tw-backdrop-blur-xl"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="tw-relative tw-bg-[#0d0d0d] tw-rounded-[32px] tw-w-full tw-max-w-xl tw-overflow-hidden tw-shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)] tw-border tw-border-white/5"
          >
            {/* Header */}
            <div className="tw-p-10 tw-flex tw-justify-between tw-items-start">
              <div>
                <h2 className="tw-text-2xl tw-font-bold tw-text-white">
                  {isRefund ? "Confirm Refund Details" : "Confirm Exchange Details"}
                </h2>
                <p className="tw-text-gray-500 tw-text-sm tw-mt-1">
                  {isRefund 
                    ? "Please review the pickup address for your return. Your refund will be processed after the item is collected." 
                    : "Please review the details below before we proceed with your replacement."}
                </p>
              </div>
              <button
                onClick={onClose}
                className="tw-absolute tw-top-8 tw-right-8 tw-p-2 tw-text-gray-500 hover:tw-text-white tw-bg-transparent tw-border-none tw-cursor-pointer tw-transition-colors"
              >
                <X className="tw-w-6 tw-h-6" />
              </button>
            </div>

            <div className="tw-px-8 tw-space-y-8">
              {/* Product Preview Card */}
              <div className="tw-bg-[#222225] tw-rounded-xl tw-p-6 tw-flex tw-items-center tw-gap-6 tw-border tw-border-white/5">
                <div className="tw-w-32 tw-h-32 tw-rounded-lg tw-overflow-hidden tw-p-2 tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center">
                  {isRefund ? (
                    <div className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-[#00C5FB]/10 tw-flex tw-items-center tw-justify-center">
                       {type === 'store_credit' ? <Wallet className="tw-w-8 tw-h-8 tw-text-[#00C5FB]" /> : <Landmark className="tw-w-8 tw-h-8 tw-text-[#00C5FB]" />}
                    </div>
                  ) : (
                    <img src={replacementProduct.image} alt="Replacement" className="tw-w-full tw-h-full tw-object-cover tw-rounded-lg" />
                  )}
                </div>
                <div className="tw-flex-1 tw-space-y-2">
                  <p className="tw-text-[10px] tw-font-black tw-tracking-[0.1em] tw-text-[#00C5FB] tw-uppercase">
                    {type === 'store_credit' ? "Store Credit Resolution" : type === 'refund' ? "Original Refund Resolution" : "Selected Replacement Product"}
                  </p>
                  <h3 className="tw-text-xl tw-font-bold tw-text-white tw-leading-tight">
                    {isRefund ? (type === 'store_credit' ? "Protega Wallet Credit" : "Refund to Original Source") : replacementProduct.title}
                  </h3>
                  <p className="tw-text-2xl tw-font-bold tw-text-[#00C5FB]">
                    {isRefund ? `₹${replacementProduct.price}` : `${replacementProduct.price} /-`}
                  </p>
                </div>
              </div>

              {/* Address Section */}
              <div className="tw-space-y-4">
                <div className="tw-flex tw-justify-between tw-items-center">
                  <h4 className="tw-text-white tw-font-bold tw-text-sm">Pickup Address</h4>
                  <button 
                    onClick={onEditAddress}
                    className="tw-text-[#00C5FB] tw-text-xs tw-font-bold tw-bg-transparent tw-border-none tw-cursor-pointer hover:tw-underline"
                  >
                    Edit
                  </button>
                </div>
                <div className="tw-bg-[#222225] tw-rounded-xl tw-overflow-hidden tw-relative tw-flex tw-border tw-border-white/5">
                  <div className="tw-w-1.5 tw-bg-[#00C5FB] tw-h-auto" />
                  <div className="tw-p-6 tw-flex tw-gap-4">
                    <div className="tw-mt-1">
                      <MapPin className="tw-w-5 tw-h-5 tw-text-gray-500" />
                    </div>
                    <div className="tw-space-y-1.5">
                       <p className="tw-text-white tw-font-bold tw-text-base">{address.fullName}</p>
                       <p className="tw-text-gray-400 tw-text-sm tw-leading-relaxed">
                         {address.street}, {address.city}, {address.state} {address.pin}
                       </p>
                       {phone && (
                         <div className="tw-flex tw-items-center tw-gap-2 tw-mt-1">
                           <span className="tw-text-[#00C5FB] tw-text-sm tw-font-bold">{phone}</span>
                         </div>
                       )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="tw-p-8 tw-flex tw-gap-4">
              <button
                onClick={onClose}
                disabled={submitting}
                className="tw-flex-1 tw-py-4 tw-rounded-md tw-bg-transparent tw-text-white tw-font-bold tw-text-sm tw-border tw-border-white/10 tw-cursor-pointer hover:tw-bg-white/5 tw-transition-all disabled:tw-opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={submitting}
                className="tw-flex-1 tw-py-4 tw-rounded-md tw-bg-[#00C5FB] tw-text-black tw-font-bold tw-text-sm tw-border-none tw-cursor-pointer hover:tw-bg-cyan-400 tw-transition-all tw-shadow-[0_0_25px_rgba(0,197,251,0.5)] disabled:tw-opacity-70 disabled:tw-cursor-not-allowed"
              >
                {submitting ? (
                  <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                    <div className="tw-w-4 tw-h-4 tw-border-2 tw-border-black tw-border-t-transparent tw-rounded-full tw-animate-spin" />
                    Processing...
                  </div>
                ) : (
                  "Confirm & Proceed"
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ResolutionConfirmationModal;
